import type { AxiosError, AxiosResponse } from 'axios';
import type { LoggedInUser } from '../../types/auth.types';
import { trackCandidateRegistrationComplete, trackEmployerRegistrationComplete } from '../analytics/gtmEvents';
import { API_METHODS, RESPONSE_TEXT } from './api.enums';
import newAPIService from './newAPIService';
import { apiAction } from './utils.actions';

export type RegisterUserBody = {
    email: string
    phone?: string
    password: string
    lastname?: string
    firstname?: string
    smartAgentByActivity?: boolean
    showToEmployers?: boolean
    coupon?: string
    isEmployer?: boolean
    companyId?: string
}

export const registerUser = async (data: RegisterUserBody): Promise<AxiosResponse<LoggedInUser>> => {
    try {
        const response = await newAPIService.post('/api/register', data);
        if (response.data.isEmployer) {
            trackEmployerRegistrationComplete({ userid: response.data.userid });
        } else {
            trackCandidateRegistrationComplete({ userid: response.data.userid });
        }
        return response;
    } catch (error) {
        let message;
        const err = error as AxiosError;
        switch (err.response.status) {
        case 400: {
            if (RESPONSE_TEXT.BAD_REQUEST === err.response.data) {
                message = 'שם משתמש או סיסמא שגויים';
            } else {
                message = err.response.data;
            }
            break;
        }
        case 419:
            message = 'כתובת המייל כבר קיימת במערכת. נסה לבצע התחברות.';
            break;
        default:
            message = 'אירעה שגיאה, אנא נסה שנית';
            break;
        }
        throw new Error(message);
    }
};

export const getLoggedUser = async (): Promise<AxiosResponse<LoggedInUser>> => newAPIService.get('/api/getLoggedUser');

/**
 * @param {Object} data
 * @param {string} data.password
 * @param {string} data.username
 */
export type LoginApiPayload = {
    password: string,
    username: string,
}
export const loginApi = async (data: LoginApiPayload) => {
    try {
        return await newAPIService.post('/login', data);
    } catch (error) {
        const err = error as AxiosError;
        let message;
        switch (err.response.status) {
        case 401:
            message = 'שם משתמש או סיסמא שגויים';
            break;
        default:
            message = 'אירעה שגיאה, אנא נסה שנית';
            break;
        }
        throw new Error(message);
    }
};

export type ResetPasswordBody = {
    email: string;
}
export const resetPassword = async (data: ResetPasswordBody) => newAPIService.post('/api/users/resetPassword', data);

export type ResetUserPasswordQuery = {
    userid: string;
    safeid?: string;
}

export type ResetUserPasswordBody = {
    password: string;
}

export const resetUserPassword = async (
    query: ResetUserPasswordQuery, data: ResetUserPasswordBody,
) => apiAction<void>({
    url: `/api/users/resetPassword/${query.userid}?safeid=${query.safeid}`,
    payload: data,
    backdrop: true,
    method: API_METHODS.POST,
});

type SendUserEmailConfirmationParams = { userid: number }
export const sendUserEmailConfirmation = async (
    params: SendUserEmailConfirmationParams,
) => apiAction<void>({
    url: `/api/message/users/${params.userid}/email-confirmation`,
    method: API_METHODS.GET,
    backdrop: true,
});
